import { SecureStorage } from '@aparajita/capacitor-secure-storage'
import { Preferences } from '@capacitor/preferences'
import { isNativeApp } from './platform'
import { getInstance } from './instance'
import { dropRejectedGrant } from './wipeAckCredential'

/**
 * Native B1 credential store (NB1-1, APPROVED 2026-10-24 — a security decision).
 *
 * On native, the organizer (`od_`) and grant (`gs_`) session tokens and the two return
 * credentials live ONLY in OS secure storage (Keychain/Keystore) via the secure-storage plugin,
 * under `orcl:${host}:${kind}` so each instance's credentials are separate. They NEVER touch
 * localStorage, `@capacitor/preferences`, IndexedDB or SQLite. An in-memory copy for the
 * current process backs the sync `getCredential` (request interceptors cannot await).
 *
 * On web every function is a no-op returning null: cookies carry the session, and the return
 * credentials stay in localStorage (the existing documented exception).
 *
 * The active mode is UI state, not a secret, so it alone goes to Preferences.
 */

export type CredentialKind = 'organizer' | 'grant' | 'canvasserReturn' | 'attendeeReturn'
export type ActiveMode = 'organizer' | 'grant'

const KINDS: readonly CredentialKind[] = ['organizer', 'grant', 'canvasserReturn', 'attendeeReturn']
const ACTIVE_MODE_KEY = 'orcl.activeMode'

/** In-memory mirror of secure storage, keyed by the full namespaced key. */
const cache = new Map<string, string>()
let activeMode: ActiveMode | null = null

function currentHost(): string | null {
  const inst = getInstance()
  return inst ? new URL(inst.origin).host : null
}

function keyFor(host: string, kind: CredentialKind): string {
  return `orcl:${host}:${kind}`
}

/** Loads the current instance's credentials (and the active mode) into memory. */
export async function hydrateCredentials(): Promise<void> {
  if (!isNativeApp()) return
  const { value } = await Preferences.get({ key: ACTIVE_MODE_KEY })
  activeMode = value === 'organizer' || value === 'grant' ? value : null
  const host = currentHost()
  if (!host) return
  for (const kind of KINDS) {
    const key = keyFor(host, kind)
    const stored = await SecureStorage.getItem(key)
    if (stored) cache.set(key, stored)
    else cache.delete(key)
  }
}

/** Sync read for the current instance; null on web or with no instance. */
export function getCredential(kind: CredentialKind): string | null {
  if (!isNativeApp()) return null
  const host = currentHost()
  if (!host) return null
  return cache.get(keyFor(host, kind)) ?? null
}

export async function setCredential(kind: CredentialKind, value: string): Promise<void> {
  if (!isNativeApp()) return
  const host = currentHost()
  // Fail closed: a credential with no instance has no namespace to live in.
  if (!host) throw new Error('No instance selected')
  const key = keyFor(host, kind)
  await SecureStorage.setItem(key, value)
  cache.set(key, value)
  // A fresh grant supersedes any rejected one kept for the wipe-ack.
  if (kind === 'grant') dropRejectedGrant()
}

export async function clearCredential(kind: CredentialKind): Promise<void> {
  if (!isNativeApp()) return
  const host = currentHost()
  if (!host) return
  const key = keyFor(host, kind)
  cache.delete(key)
  await SecureStorage.removeItem(key)
}

export async function clearAllCredentialsForInstance(): Promise<void> {
  if (!isNativeApp()) return
  dropRejectedGrant()
  for (const kind of KINDS) await clearCredential(kind)
}

export function getActiveMode(): ActiveMode | null {
  if (!isNativeApp()) return null
  return activeMode
}

export async function setActiveMode(m: ActiveMode): Promise<void> {
  if (!isNativeApp()) return
  await Preferences.set({ key: ACTIVE_MODE_KEY, value: m })
  activeMode = m
}
